import React from 'react';
import PropTypes, { shape, oneOfType, arrayOf } from 'prop-types';
import { NAVLINK_VARIANTS } from '../../../../common/constants/navbar-link-variants';
import {
  NavColumn,
  NavSectionHeading,
  NavItems,
} from './navbar-columns.styles';
import { NavbarContentLink } from '../../atoms/navbar-atoms/navbar-content-link/navbar-content-link.tsx';
import { NavbarQuickLink } from '../../atoms/navbar-atoms/navbar-quick-link/navbar-quick-link';
import { NavbarDescriptionLink } from '../../atoms/navbar-atoms/navbar-description-link/navbar-description-link';
import { NavbarIconLink } from '../../atoms/navbar-atoms/navbar-icon-link/navbar-icon-link';
import { NavbarSuperlink } from '../../atoms/navbar-atoms/navbar-superlink/navbar-superlink';

export const NavbarColumns = ({ data, handleReset }) => {
  if (!data) return null; // return early

  return data.map((column, i) => {
    const isLastColumn = i + 1 === data.length;

    return (
      <NavColumn
        key={i}
        background={column.backgroundColor}
        data-testid={`nav-column`}
        gridAreaIndex={i + 1}
      >
        {column.sections.map((section, index) => (
          <div
            key={`${section.title.toLowerCase().split(' ').join('-')}-${index}`}
          >
            <NavSectionHeading>{section.title}</NavSectionHeading>
            <NavItems
              columnWidth={section.columnWidth}
              isLastColumn={isLastColumn}
            >
              {section.items?.map((item, idx) => {
                if (item.variant === NAVLINK_VARIANTS.CONTENT_LINK) {
                  return (
                    <NavbarContentLink
                      key={idx}
                      item={item}
                      columnNumber={i + 1}
                      hasBackground={!!column.backgroundColor}
                      handleReset={handleReset}
                    />
                  );
                }
                if (item.variant === NAVLINK_VARIANTS.DESCRIPTION_LINK) {
                  return (
                    <NavbarDescriptionLink
                      key={idx}
                      item={item}
                      columnNumber={i + 1}
                      hasBackground={!!column.backgroundColor}
                      handleReset={handleReset}
                    />
                  );
                }
                if (item.variant === NAVLINK_VARIANTS.ICON_LINK) {
                  return (
                    <NavbarIconLink
                      item={item}
                      key={idx}
                      columnNumber={i + 1}
                      hasBackground={!!column.backgroundColor}
                    />
                  );
                }

                return (
                  <NavbarQuickLink
                    key={idx}
                    item={item}
                    columnNumber={i + 1}
                    hasBackground={!!column.backgroundColor}
                    handleReset={handleReset}
                  />
                );
              })}
            </NavItems>
            {section.superlinkTitle && section.superlinkPath && (
              <NavbarSuperlink
                title={section.superlinkTitle}
                path={section.superlinkPath}
                columnNumber={i + 1}
                hasBackground={!!column.backgroundColor}
              />
            )}
          </div>
        ))}
      </NavColumn>
    );
  });
};

export const NavbarColumnProps = {
  backgroundColor: PropTypes.string,
  sections: arrayOf(
    oneOfType([
      shape({
        item: PropTypes.shape({
          variant: PropTypes.oneOf([NAVLINK_VARIANTS.CONTENT_LINK]).isRequired,
          title: PropTypes.string.isRequired,
          image: PropTypes.shape({
            file: PropTypes.shape({
              url: PropTypes.string.isRequired,
            }),
            description: PropTypes.string,
          }),
          isExternalLink: PropTypes.bool,
          linkPath: PropTypes.string,
        }),
        columnNumber: PropTypes.number,
        hasBackground: PropTypes.bool,
        handleReset: PropTypes.func,
        translate: PropTypes.bool,
      }),
      shape({
        item: PropTypes.shape({
          variant: PropTypes.oneOf([NAVLINK_VARIANTS.DESCRIPTION_LINK])
            .isRequired,
          title: PropTypes.string.isRequired,
          description: PropTypes.string.isRequired,
          pillColor: PropTypes.string,
          pillTitle: PropTypes.string,
          isExternalLink: PropTypes.bool,
          linkPath: PropTypes.string,
        }),
        columnNumber: PropTypes.number,
        hasBackground: PropTypes.bool,
        handleReset: PropTypes.func,
      }),
      shape({
        item: PropTypes.shape({
          variant: PropTypes.oneOf([NAVLINK_VARIANTS.ICON_LINK]).isRequired,
          title: PropTypes.string.isRequired,
          icon: PropTypes.oneOf(['article', 'video', 'podcast', 'news']),
          iconPosition: PropTypes.oneOf(['left', 'right']),
          isExternalLink: PropTypes.bool,
          linkPath: PropTypes.string,
        }),
        columnNumber: PropTypes.number,
        hasBackground: PropTypes.bool,
      }),
      shape({
        item: PropTypes.shape({
          variant: PropTypes.oneOf([NAVLINK_VARIANTS.QUICK_LINK]).isRequired,
          title: PropTypes.string.isRequired,
          bulletColor: PropTypes.string.isRequired,
          isExternalLink: PropTypes.bool,
          linkPath: PropTypes.string,
          pillColor: PropTypes.string,
          pillTitle: PropTypes.string,
        }),
        columnNumber: PropTypes.number,
        hasBackground: PropTypes.bool,
        handleReset: PropTypes.func,
      }),
    ])
  ),
};

NavbarColumns.propTypes = {
  handleReset: PropTypes.func,
  data: arrayOf(shape(NavbarColumnProps)),
};
